import { Component, type ReactNode, type ErrorInfo } from 'react';
import { captureException } from '@butter/monitoring/web';
import { classifyError, ERROR_TITLES, type ClassifiedError } from './errorTypes';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  classifiedError?: ClassifiedError;
}

/**
 * Error Boundary Component
 * Catches React errors and displays appropriate UI
 * 
 * Shows detailed errors in dev/staging, simple message in production
 * Now properly classifies errors for better user feedback
 */
class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const classifiedError = classifyError(error);
    return { hasError: true, error, classifiedError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const classifiedError = classifyError(error);
    this.setState({ error, errorInfo, classifiedError });
    
    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught error:', {
        classified: classifiedError,
        original: error,
        errorInfo
      });
    }

    captureException(error, {
      tags: { errorType: classifiedError.type },
      extra: { componentStack: errorInfo.componentStack ?? undefined },
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, classifiedError: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI with classified error
      return (
        <ErrorFallback
          error={this.state.error!}
          classifiedError={this.state.classifiedError}
          errorInfo={this.state.errorInfo}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

export interface ErrorFallbackProps {
  error: Error;
  classifiedError?: ClassifiedError;
  errorInfo?: ErrorInfo;
  resetError: () => void;
}

export function ErrorFallback({ error, classifiedError, errorInfo, resetError }: ErrorFallbackProps) {
  const isDev = import.meta.env.DEV;

  // Use classified error message if available, fallback to raw error
  const displayMessage = classifiedError?.userMessage || error.message || 'An unexpected error occurred';
  const errorType = classifiedError ? ERROR_TITLES[classifiedError.type] : 'Error';
  const isRetryable = classifiedError?.retryable ?? true;
  const requiresSupport = classifiedError?.requiresSupport ?? false;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-card border border-border rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0">
            <svg
              className="w-8 h-8 text-destructive"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {errorType}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isDev
                ? 'An error occurred in the application. See details below.'
                : displayMessage}
            </p>
          </div>
        </div>

        {/* User-friendly Error Message */}
        <div className="mb-6">
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
            <p className="text-sm text-destructive">
              {displayMessage}
            </p>
            {requiresSupport && (
              <p className="text-xs text-muted-foreground mt-2">
                If this issue persists, please contact support with error code: {errorType}
              </p>
            )}
          </div>
        </div>

        {/* Stack Trace (Dev Only) */}
        {isDev && error.stack && (
          <details className="mb-6">
            <summary className="cursor-pointer text-sm font-medium text-foreground mb-2 hover:text-primary">
              Stack Trace
            </summary>
            <div className="bg-muted rounded-md p-4 overflow-auto max-h-[300px]">
              <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                {error.stack}
              </pre>
            </div>
          </details>
        )}

        {/* Component Stack (Dev Only) */}
        {isDev && errorInfo?.componentStack && (
          <details className="mb-6">
            <summary className="cursor-pointer text-sm font-medium text-foreground mb-2 hover:text-primary">
              Component Stack
            </summary>
            <div className="bg-muted rounded-md p-4 overflow-auto max-h-[300px]">
              <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                {errorInfo.componentStack}
              </pre>
            </div>
          </details>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {isRetryable && (
            <button
              onClick={resetError}
              className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm font-medium transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Try Again
            </button>
          )}
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm font-medium transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Reload Page
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="inline-flex items-center px-4 py-2 border border-border bg-background rounded-md hover:bg-accent text-sm font-medium transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}

// Export the class component wrapped as ErrorBoundary
export function ErrorBoundary(props: ErrorBoundaryProps) {
  return <ErrorBoundaryClass {...props} />;
}
